import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  LiveHelp as LiveHelpIcon,
  HeadsetMic as HeadsetMicIcon, // New support icon
  QuestionAnswer as QuestionAnswerIcon, // New FAQ icon
} from "@mui/icons-material";
import api from "../../utils/api";
import { errorAlert } from "../../utils/swalConfig";

const Support = () => {
  const [supportSettings, setSupportSettings] = useState({
    whatsappNumber: "",
    supportEmail: "",
    faqs: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSupportSettings();
  }, []);

  const fetchSupportSettings = async () => {
    try {
      const token = localStorage.getItem("jwt");
      const response = await api.get("/users/support/settings", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSupportSettings(response.data.data);
      setLoading(false);
    } catch (err) {
      errorAlert("Error", "Failed to fetch support settings");
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    if (supportSettings.whatsappNumber) {
      window.open(`https://wa.me/${supportSettings.whatsappNumber}`, "_blank");
    }
  };

  const handleEmailClick = () => {
    if (supportSettings.supportEmail) {
      window.location.href = `mailto:${supportSettings.supportEmail}`;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ display: "flex", alignItems: "center", gap: 1 }}
      >
        <HeadsetMicIcon sx={{ fontSize: 32, color: "primary.main" }} />
        Help & Support
      </Typography>

      {/* Contact Options */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: "100%",
              transition: "transform 0.2s",
              "&:hover": { transform: "translateY(-4px)" },
            }}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  bgcolor: "#25D366",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 2,
                }}
              >
                <WhatsAppIcon sx={{ fontSize: 40, color: "white" }} />
              </Box>
              <Typography variant="h6" gutterBottom>
                WhatsApp Support
              </Typography>
              <Typography color="textSecondary" sx={{ mb: 2 }}>
                Get instant support via WhatsApp
              </Typography>
              <Button
                variant="contained"
                startIcon={<WhatsAppIcon />}
                onClick={handleWhatsAppClick}
                sx={{
                  bgcolor: "#25D366",
                  "&:hover": { bgcolor: "#128C7E" },
                }}
              >
                Chat on WhatsApp
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: "100%",
              transition: "transform 0.2s",
              "&:hover": { transform: "translateY(-4px)" },
            }}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  bgcolor: "primary.main",
                  width: 80,
                  height: 80,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto",
                  mb: 2,
                }}
              >
                <EmailIcon sx={{ fontSize: 40, color: "white" }} />
              </Box>
              <Typography variant="h6" gutterBottom>
                Email Support
              </Typography>
              <Typography color="textSecondary" sx={{ mb: 2 }}>
                Send us an email for detailed inquiries
              </Typography>
              <Button
                variant="contained"
                startIcon={<EmailIcon />}
                onClick={handleEmailClick}
              >
                Send Email
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* FAQs Section */}
      <Card>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              mb: 3,
              borderBottom: 1,
              borderColor: "divider",
              pb: 2,
            }}
          >
            <QuestionAnswerIcon
              sx={{
                mr: 1,
                color: "primary.main",
                fontSize: 32,
              }}
            />
            <Typography variant="h6">Frequently Asked Questions</Typography>
          </Box>

          {supportSettings.faqs.map((faq, index) => (
            <Accordion key={index}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight="medium">{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>
    </Box>
  );
};

export default Support;
